import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BatchGenerator = ({ trails, onBatchGenerate }) => {
  const [selectedTrails, setSelectedTrails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedTrails(trails?.map(t => t?.id));
    } else {
      setSelectedTrails([]);
    }
  };

  const handleTrailToggle = (trailId) => {
    setSelectedTrails(prev => {
      if (prev?.includes(trailId)) {
        return prev?.filter(id => id !== trailId);
      } else {
        return [...prev, trailId];
      }
    });
  };

  const handleGenerate = () => {
    const selected = trails?.filter(t => selectedTrails?.includes(t?.id));
    onBatchGenerate(selected);
  };

  return (
    <div className="bg-card rounded-lg shadow-sm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Batch Generation</h2>
          <p className="text-sm text-muted-foreground mt-1">Generate multiple QR codes at once</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {selectedTrails?.length} selected
          </span>
        </div>
      </div>
      <div className="mb-4">
        <Checkbox
          label="Select All Trails"
          checked={selectAll}
          onChange={(e) => handleSelectAll(e?.target?.checked)}
        />
      </div>
      <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
        {trails?.map((trail) => (
          <div
            key={trail?.id}
            className={`bg-background border rounded-lg p-4 transition-organic ${
              selectedTrails?.includes(trail?.id)
                ? 'border-primary shadow-sm'
                : 'border-border'
            }`}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                checked={selectedTrails?.includes(trail?.id)}
                onChange={() => handleTrailToggle(trail?.id)}
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-1 line-clamp-1">
                  {trail?.title}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-2 line-clamp-2">
                  {trail?.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center">
                    <Icon name="Users" size={14} className="mr-1" />
                    <span>{trail?.classroom}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="BookOpen" size={14} className="mr-1" />
                    <span>{trail?.subject}</span>
                  </div>
                  <div>
                    <span>{trail?.studentsAssigned} students</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedTrails?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Batch Generation Info</h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Each selected trail will generate a separate QR code. All codes will be compiled into a single PDF document with clear labels for easy distribution.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          iconName="X"
          onClick={() => {
            setSelectedTrails([]);
            setSelectAll(false);
          }}
          disabled={selectedTrails?.length === 0}
          fullWidth
        >
          Clear Selection
        </Button>
        <Button
          variant="default"
          iconName="Download"
          iconPosition="left"
          onClick={handleGenerate}
          disabled={selectedTrails?.length === 0}
          fullWidth
        >
          Generate {selectedTrails?.length} QR Code{selectedTrails?.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default BatchGenerator;